import React from 'react';
import { styled } from '@compiled/react';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { colors, fontSizeSmall } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import type { TrendColorScheme } from '../../types';
import { getTrend } from '../../utils/trend';
import { TrendIcon as TrendIconCommon } from '../trend-icon';
import type { Props, TrendIconProps } from './types';
import {
	useDeploymentsComparisonMessageTranslated,
	useIssuesComparisonMessageTranslated,
} from './utils';

const TrendIcon = ({ prodDataState, trend, colorScheme }: TrendIconProps) => {
	if (prodDataState === 'SUFFICIENT_FOR_TREND') {
		return <TrendIconCommon trend={trend} colorScheme={colorScheme} />;
	}

	return <InfoIcon label="" primaryColor={token('color.icon', colors.N100)} size="small" />;
};

type TrendSummaryProps = Props & {
	comparisonMessage: string;
	colorScheme: TrendColorScheme;
};

const TrendSummary = ({
	comparisonMessage,
	weekCount,
	wholePeriodStatistic,
	prodDataState,
	colorScheme = 'UP_IS_GOOD',
}: TrendSummaryProps) => {
	const trend = getTrend(weekCount, wholePeriodStatistic);
	const noTrendIcon = trend === 'equal' && prodDataState === 'SUFFICIENT_FOR_TREND';
	return (
		<Comparison>
			<IconWrapper noMargin={noTrendIcon}>
				<TrendIcon prodDataState={prodDataState} trend={trend} colorScheme={colorScheme} />
			</IconWrapper>
			<ComparisonText>{comparisonMessage}</ComparisonText>
		</Comparison>
	);
};

export const DeploymentsTrendSummary = ({
	weekCount,
	wholePeriodStatistic,
	prodDataState,
}: Props) => {
	const comparisonMessage = useDeploymentsComparisonMessageTranslated(
		weekCount,
		wholePeriodStatistic,
		prodDataState,
	);
	return (
		<TrendSummary
			comparisonMessage={comparisonMessage}
			weekCount={weekCount}
			wholePeriodStatistic={wholePeriodStatistic}
			prodDataState={prodDataState}
			colorScheme="UP_IS_GOOD"
		/>
	);
};

export const IssuesTrendSummary = ({ weekCount, wholePeriodStatistic, prodDataState }: Props) => {
	const comparisonMessage = useIssuesComparisonMessageTranslated(
		weekCount,
		wholePeriodStatistic,
		prodDataState,
	);
	return (
		<TrendSummary
			comparisonMessage={comparisonMessage}
			weekCount={weekCount}
			wholePeriodStatistic={wholePeriodStatistic}
			prodDataState={prodDataState}
			colorScheme="UP_IS_BAD"
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Comparison = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.span<{ noMargin: any }>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	({ noMargin }) => (noMargin ? '' : `margin-right: ${gridSize / 2}px;`),
	{
		display: 'flex',
		alignItems: 'center',
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ComparisonText = styled.span({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSizeSmall()}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N400),
});
